import { toast, widgetTargetLabel } from "@playbookai/ui";
import type { WidgetCommandResult } from "../api/playbook-be-client.js";

/**
 * PB-4923 — tell the person which parts of their save did not land.
 *
 * A widget command answers 200 even when some operations were skipped: a skip
 * is an OUTCOME, not a failure. That makes it the one response shape a client
 * can quietly mis-read as success, so this exists to make the difference
 * visible rather than leaving it in a field nobody renders.
 *
 * Two rules the copy has to obey, both from the plan:
 *
 * **Never invent a name.** An attributed skip carries `actorId`, and a client
 * cannot turn a user id into a name on its own. A resolver IS wired now
 * (`WidgetActorNameProvider`, fed from the workspace's roster and presence,
 * both already in memory), but it may still answer `null` — a former member, or
 * somebody the roster has not resolved yet — and then the copy stays neutral:
 * "Someone else", never a guess and never an id fragment. An ABSENT `actorId`
 * is a distinct case and takes the same neutral wording.
 *
 * **Say what happened to the content, not what the server called it.** A person
 * whose edit landed on a deleted cause needs to know the cause is gone, not
 * that an operation was skipped with reason `item_deleted`. The field is named
 * from the widget's own content, so it reads as the text on screen rather than
 * as `branches[br-1]/causes[c-2]/text`.
 */

/**
 * Human-facing copy per skip reason.
 *
 * `editing_in_progress` and `field_changed` are deliberately absent from the
 * human path: the executor only produces them for an AI source, because a
 * person is never blocked by somebody's editing session and never carries a
 * baseline. They are handled anyway, so an unexpected one is still reported
 * rather than silently dropped.
 */
const REASON_COPY: Record<string, string> = {
  item_deleted: "It was deleted while you were editing.",
  invalid: "The change would have left the widget in an invalid state.",
  capacity_exceeded: "This list is already at its maximum size.",
  unsupported_target: "That part of this widget can't be edited this way yet.",
  dependency_skipped: "It was part of a change that couldn't be applied.",
  editing_in_progress: "Someone else is editing it right now.",
  field_changed: "Someone else changed it first.",
};

/**
 * Raise ONE notice for a command whose result carries skipped operations.
 *
 * One notice rather than one per operation: a single gesture can produce
 * several skips for the same cause, and a stack of near-identical toasts is
 * worse than one that names the count. `dedupeKey` is per widget, so a rapid
 * sequence of saves against the same widget replaces its notice rather than
 * piling up.
 */
export function notifySkippedWidgetOperations(
  result: WidgetCommandResult,
  attribution?: {
    /** Resolves a user id to a display name, or `null` when it cannot. */
    readonly nameForActor: (userId: string) => string | null;
  },
): void {
  if (result.skipped.length === 0) return;

  const reasons = [...new Set(result.skipped.map((entry) => entry.reason))];
  /**
   * AC6 — one skip gets the specific sentence: which field, and who.
   *
   * Only ONE. With several skips there is no single field or person to name,
   * and a toast that lists them is worse than one that gives the count and lets
   * the reconciled content speak for itself.
   *
   * Both halves degrade independently and on purpose. An unresolvable field
   * (the item was deleted, which is exactly the `item_deleted` case) falls back
   * to the reason copy; an unresolvable actor falls back to "Someone else",
   * never to an id fragment. Naming the wrong person is worse than naming none.
   */
  const specific =
    result.skipped.length === 1
      ? attributedSkip(result.skipped[0]!, result.params, attribution)
      : null;

  const detail =
    specific ??
    (reasons.length === 1
      ? (REASON_COPY[reasons[0]!] ?? "It couldn't be applied.")
      : "Some of your changes couldn't be applied.");

  const title =
    result.applied.length > 0
      ? `Saved, except ${countLabel(result.skipped.length)}`
      : "Your change wasn't saved";

  toast(detail, {
    title,
    // `info` rather than `critical`: the rest of the save landed, and the
    // content on screen is about to be reconciled to server truth. A red toast
    // would over-report a partial success.
    tone: result.applied.length > 0 ? "info" : "watch",
    dedupeKey: `widget-skipped:${result.widgetId}`,
  });
}

function countLabel(count: number): string {
  return count === 1 ? "one change" : `${count} changes`;
}

/**
 * The AC6 sentence: WHICH FIELD, and WHICH PERSON.
 *
 * Returns `null` when it cannot do better than the generic reason copy, so the
 * caller keeps exactly the behaviour it had before rather than degrading to
 * something half-named.
 *
 * The field name comes from the widget's own CONTENT — `result.params` is the
 * new authoritative params, so the item is named by the text a person can see
 * on screen ("Machine calibration"), not by an id or a schema field name.
 */
function attributedSkip(
  skip: WidgetCommandResult["skipped"][number],
  params: unknown,
  attribution?: { readonly nameForActor: (userId: string) => string | null },
): string | null {
  const field = widgetTargetLabel(skip.targetKey, params);
  if (field === null) return null;

  const name =
    skip.actorId === undefined
      ? null
      : (attribution?.nameForActor(skip.actorId) ?? null);
  const who = name ?? "Someone else";

  switch (skip.reason) {
    case "editing_in_progress":
      return `${who} is editing ${field} right now.`;
    case "field_changed":
      return `${who} changed ${field} first.`;
    case "item_deleted":
      return `${field} was deleted while you were editing.`;
    default:
      return null;
  }
}

/**
 * Raise a notice for operations the CLIENT dropped while rebasing onto fresh
 * server content — before they were ever sent.
 *
 * This is the other half of AC7 and it happens without any request: a peer
 * deletes the cause you are typing into, the nudge brings their deletion, and
 * your outstanding edit no longer has a target. Retrying it forever would
 * resurrect the item the plan says must stay deleted, so it is dropped — and
 * dropping someone's typing silently is exactly what this whole change exists
 * to stop, so it is said out loud.
 */
export function notifyDroppedWidgetOperations(
  widgetId: string,
  dropped: ReadonlyArray<{ readonly reason: string }>,
): void {
  if (dropped.length === 0) return;
  const reasons = [...new Set(dropped.map((entry) => entry.reason))];
  const detail =
    reasons.length === 1 && reasons[0] === "item_deleted"
      ? "It was deleted by someone else while you were editing."
      : "It no longer applies to the current version of this widget.";

  toast(detail, {
    title:
      dropped.length === 1
        ? "Your change wasn't saved"
        : `${dropped.length} of your changes weren't saved`,
    tone: "watch",
    dedupeKey: `widget-dropped:${widgetId}`,
  });
}

/**
 * AC5, the honest failure. Repeated attempts to register an editing session
 * failed, so the server does not know this person is here — and an approved
 * Player AI change will not skip the field they are typing in.
 *
 * Said plainly rather than swallowed. The client cannot make the protection
 * work on its own (it lives in a server-side session), so the only thing it
 * owes is not to let somebody believe in a protection they do not have. Their
 * committed edits are still safe: a field's revision is what guards those, and
 * that does not depend on a session existing.
 *
 * Deduped per widget, because one unreachable server means every field of it
 * fails and a toast per control would bury the message it is making.
 */
export function notifyEditSessionUnavailable(widgetId: string): void {
  toast(
    "Your edits will still save. Player AI just won't know to skip this field while you type.",
    {
      title: "Couldn't flag this field as being edited",
      tone: "watch",
      dedupeKey: `widget-lease:${widgetId}`,
    },
  );
}
